import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { aiService, type AIInsight } from '../services/aiService'
import type { AITaskSuggestion } from '../types/task'
import { 
  SparklesIcon,
  LightBulbIcon,
  ChartBarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  PlayIcon
} from '@heroicons/react/24/outline'

interface AIInsightsDashboardProps {
  isOpen: boolean
  onClose: () => void
  onAcceptSuggestion?: (suggestion: AITaskSuggestion) => void
}

const AIInsightsDashboard = ({ isOpen, onClose, onAcceptSuggestion }: AIInsightsDashboardProps) => {
  const { user } = useAuth()
  const [insights, setInsights] = useState<AIInsight[]>([])
  const [suggestions, setSuggestions] = useState<AITaskSuggestion[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'insights' | 'suggestions' | 'workflows'>('insights')

  useEffect(() => {
    if (isOpen && user) {
      loadAIData()
    }
  }, [isOpen, user])

  const loadAIData = async () => {
    if (!user) return
    
    setLoading(true)
    try {
      const [insightsData, suggestionsData] = await Promise.all([
        aiService.generateInsights(user.id),
        aiService.generateTaskSuggestions(user.id, {})
      ])
      
      setInsights(insightsData)
      setSuggestions(suggestionsData)
    } catch (error) {
      console.error('Error loading AI data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getInsightIcon = (type: AIInsight['type']) => {
    switch (type) {
      case 'productivity': return ClockIcon
      case 'revenue': return ArrowTrendingUpIcon
      case 'efficiency': return ChartBarIcon
      case 'workload': return ExclamationTriangleIcon
      default: return LightBulbIcon
    }
  }

  const getInsightColor = (type: AIInsight['type']) => {
    switch (type) {
      case 'productivity': return 'from-blue-500 to-cyan-500'
      case 'revenue': return 'from-emerald-500 to-green-500'
      case 'efficiency': return 'from-purple-500 to-pink-500'
      case 'workload': return 'from-orange-500 to-red-500'
      default: return 'from-gray-500 to-gray-600'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-400 bg-red-500/20'
      case 'high': return 'text-orange-400 bg-orange-500/20'
      case 'medium': return 'text-yellow-400 bg-yellow-500/20'
      case 'low': return 'text-green-400 bg-green-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const handleAcceptSuggestion = (suggestion: AITaskSuggestion) => {
    onAcceptSuggestion?.(suggestion)
    setSuggestions(prev => prev.filter(s => s.id !== suggestion.id))
  }

  const handleDismissSuggestion = (suggestionId: string) => {
    setSuggestions(prev => prev.filter(s => s.id !== suggestionId))
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-6xl max-h-[90vh] glass-mockup rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">AI Business Intelligence</h2>
                <p className="text-gray-400 text-sm">Intelligent insights and automation for your creative business</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            {[
              { id: 'insights', label: 'Business Insights', icon: ChartBarIcon },
              { id: 'suggestions', label: 'Task Suggestions', icon: LightBulbIcon },
              { id: 'workflows', label: 'Automated Workflows', icon: PlayIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-6 py-4 transition-colors ${
                  activeTab === tab.id
                    ? 'text-white border-b-2 border-purple-500 bg-purple-500/10'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-400">Analyzing your business data...</p>
              </div>
            ) : (
              <>
                {/* Business Insights Tab */}
                {activeTab === 'insights' && (
                  <div className="space-y-6">
                    {insights.length > 0 ? (
                      insights.map((insight) => {
                        const Icon = getInsightIcon(insight.type)
                        return (
                          <motion.div
                            key={insight.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="glass-mockup rounded-xl p-6"
                          >
                            <div className="flex items-start space-x-4">
                              <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${getInsightColor(insight.type)} flex items-center justify-center flex-shrink-0`}>
                                <Icon className="w-6 h-6 text-white" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <h3 className="text-white font-semibold">{insight.title}</h3>
                                  <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full">
                                    {insight.confidence}% confidence
                                  </span>
                                </div>
                                <p className="text-gray-300 mb-3">{insight.description}</p>
                                <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                                  <p className="text-blue-400 text-sm">
                                    <strong>💡 Recommendation:</strong> {insight.recommendation}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )
                      })
                    ) : (
                      <div className="text-center py-12">
                        <ChartBarIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-white mb-2">No insights available yet</h3>
                        <p className="text-gray-400">
                          Create more tasks, shoots, and invoices to generate AI-powered business insights.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Task Suggestions Tab */}
                {activeTab === 'suggestions' && (
                  <div className="space-y-4">
                    {suggestions.length > 0 ? (
                      suggestions.map((suggestion) => (
                        <motion.div
                          key={suggestion.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`glass-mockup rounded-xl p-6 ${
                            suggestion.isLearningNotice 
                              ? 'border-2 border-blue-500/30 bg-blue-500/10' 
                              : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-3">
                                <h3 className="text-white font-semibold">{suggestion.title}</h3>
                                {!suggestion.isLearningNotice && (
                                  <>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(suggestion.priority)}`}>
                                      {suggestion.priority}
                                    </span>
                                    <span className="text-xs px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full">
                                      {suggestion.estimatedMinutes}min
                                    </span>
                                  </>
                                )}
                                {suggestion.isLearningNotice && (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                                    Learning Mode
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-300 mb-3">{suggestion.description}</p>
                              {!suggestion.isLearningNotice && (
                                <div className="flex items-center space-x-4 text-sm text-gray-400">
                                  <span>Category: {suggestion.category.replace('_', ' ')}</span>
                                  <span>•</span>
                                  <span>Confidence: {suggestion.confidence}%</span>
                                  <span>•</span>
                                  <span>Based on: {suggestion.basedOn}</span>
                                </div>
                              )}
                              <div className={`mt-3 p-3 rounded-lg border ${
                                suggestion.isLearningNotice 
                                  ? 'bg-blue-500/10 border-blue-500/30'
                                  : 'bg-purple-500/10 border-purple-500/30'
                              }`}>
                                <p className={`text-sm ${
                                  suggestion.isLearningNotice ? 'text-blue-400' : 'text-purple-400'
                                }`}>
                                  <strong>🤖 AI {suggestion.isLearningNotice ? 'Learning' : 'Reasoning'}:</strong> {suggestion.reasoning}
                                </p>
                              </div>
                            </div>
                            {!suggestion.isLearningNotice && (
                              <div className="flex items-center space-x-2 ml-4">
                                <button
                                  onClick={() => handleAcceptSuggestion(suggestion)}
                                  className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg transition-colors text-sm font-medium"
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => handleDismissSuggestion(suggestion.id)}
                                  className="px-4 py-2 bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 rounded-lg transition-colors text-sm font-medium"
                                >
                                  Dismiss
                                </button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <LightBulbIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-white mb-2">No suggestions available</h3>
                        <p className="text-gray-400">
                          AI will generate intelligent task suggestions based on your shoots, clients, and work patterns.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Automated Workflows Tab */}
                {activeTab === 'workflows' && (
                  <div className="space-y-6">
                    <div className="text-center py-12">
                      <PlayIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">Automated Workflows</h3>
                      <p className="text-gray-400 mb-6">
                        Set up intelligent automation rules to create tasks automatically based on triggers like new shoots, client additions, or approaching deadlines.
                      </p>
                      <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 transition-all">
                        Create Your First Workflow
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default AIInsightsDashboard 